import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabaseClient';
import ImageUploadZone from '../../proof-submission/components/ImageUploadZone';
import { compressImageUnderBytes } from '../../../utils/imageCompression';
import { makeSafeObjectPath, uploadToBucket } from '../../../utils/storage';

const RegisterForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [avatarImage, setAvatarImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [notice, setNotice] = useState({
    open: false,
    title: '',
    message: '',
    variant: 'info',
    primaryLabel: '확인',
    onPrimary: null,
  });

  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

  const closeNotice = () => {
    const cb = notice?.onPrimary;
    setNotice((prev) => ({
      ...prev,
      open: false,
      onPrimary: null,
    }));
    if (typeof cb === 'function') cb();
  };

  const openNotice = ({ title, message, variant = 'info', primaryLabel = '확인', onPrimary = null }) => {
    setNotice({ open: true, title, message, variant, primaryLabel, onPrimary });
  };

  const getRegisterErrorMessage = (error) => {
    const msg = String(error?.message || '').toLowerCase();
    const status = error?.status;

    if (msg.includes('user already registered') || msg.includes('already registered')) {
      return '이미 가입된 이메일입니다. 로그인하거나 다른 이메일을 사용해주세요.';
    }
    if (msg.includes('password') && (msg.includes('weak') || msg.includes('strength') || msg.includes('short'))) {
      return '비밀번호가 보안 정책을 충족하지 않습니다. (8자 이상, 대소문자/숫자/특수문자 포함)';
    }
    if (status === 429 || msg.includes('rate limit') || msg.includes('too many requests')) {
      return '요청이 너무 많습니다. 잠시 후 다시 시도해주세요.';
    }
    if (msg.includes('invalid') && msg.includes('email')) {
      return '이메일 형식을 다시 확인해주세요.';
    }

    return error?.message || '회원가입에 실패했습니다. 잠시 후 다시 시도해주세요.';
  };

  const handleAvatarSelect = (image, error) => {
    setAvatarImage(image);
    if (error) {
      setErrors((prev) => ({ ...prev, avatar: error }));
    } else {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.avatar;
        return next;
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.username) {
      newErrors.username = '사용자 이름을 입력해주세요';
    } else if (formData?.username?.length < 2) {
      newErrors.username = '사용자 이름은 최소 2자 이상이어야 합니다';
    } else if (formData?.username?.length > 20) {
      newErrors.username = '사용자 이름은 최대 20자까지 가능합니다';
    }

    if (!formData?.email) {
      newErrors.email = '이메일을 입력해주세요';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다';
    }

    if (!formData?.password) {
      newErrors.password = '비밀번호를 입력해주세요';
    } else if (formData?.password?.length < 8) {
      newErrors.password = '비밀번호는 최소 8자 이상이어야 합니다';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = '비밀번호 확인을 입력해주세요';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = '비밀번호가 일치하지 않습니다';
    }

    if (!agreedToTerms) {
      newErrors.terms = '이용약관에 동의해주세요';
    }

    if (!agreedToPrivacy) {
      newErrors.privacy = '개인정보처리방침에 동의해주세요';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors)?.length > 0) {
      console.warn('[RegisterForm] validateForm errors', newErrors);
    }

    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      console.warn('[RegisterForm] validateForm failed', {
        username: formData?.username,
        email: formData?.email,
        agreedToTerms,
        agreedToPrivacy,
      });
      return;
    }

    setIsLoading(true);

    try {
      setErrors((prev) => ({ ...prev, form: '' }));

      const { data, error } = await supabase.auth.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          data: {
            username: formData?.username,
          },
        },
      });

      console.log('[RegisterForm] signUp response', {
        data,
        error,
      });

      if (error) {
        console.error('[RegisterForm] signUp error', error);
        openNotice({
          title: '회원가입 실패',
          message: getRegisterErrorMessage(error),
          variant: 'error',
        });
        return;
      }

      const userId = data?.user?.id;

      if (userId) {
        const { data: profileData, error: profileError } = await supabase.from('ch_profiles').upsert({
          id: userId,
          username: formData?.username,
          role: 'user',
        });

        console.log('[RegisterForm] profile upsert response', {
          profileData,
          profileError,
        });

        if (profileError) {
          console.error('[RegisterForm] profile upsert error', profileError);
          openNotice({
            title: '회원가입 실패',
            message: profileError?.message || '프로필 저장에 실패했습니다. 다시 시도해주세요.',
            variant: 'error',
          });
          return;
        }

        if (avatarImage?.file) {
          try {
            const file = avatarImage.file;
            let blob = file;
            if (file.size > 1024 * 1024) {
              blob = await compressImageUnderBytes(file, 1024 * 1024);
            }

            const path = makeSafeObjectPath({ userId, originalName: file?.name, prefix: 'avatars' });
            const { error: uploadError } = await uploadToBucket({
              bucket: 'profile-avatars',
              path,
              blob,
              contentType: blob?.type || 'image/jpeg',
              cacheControl: '31536000',
            });

            if (uploadError) {
              console.error('[RegisterForm] avatar upload error', uploadError);
            } else {
              const { data: publicUrlData } = supabase.storage.from('profile-avatars').getPublicUrl(path);
              const avatarUrl = publicUrlData?.publicUrl || null;
              await supabase.from('ch_profiles').update({ avatar_url: avatarUrl }).eq('id', userId);
            }
          } catch (e) {
            console.error('[RegisterForm] avatar upload unexpected error', e);
          }
        }
      }

      if (!data?.session) {
        openNotice({
          title: '회원가입 완료',
          message: '회원가입이 완료되었습니다. 로그인 탭에서 로그인해주세요.',
          variant: 'success',
        });
        return;
      }

      navigate('/project-dashboard');
    } catch (err) {
      console.error('[RegisterForm] unexpected error', err);
      openNotice({
        title: '회원가입 실패',
        message: err?.message || '회원가입 중 오류가 발생했습니다. 다시 시도해주세요.',
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrength = () => {
    const password = formData?.password;
    if (!password) return { strength: 0, label: '', color: '' };

    let strength = 0;
    if (password?.length >= 8) strength++;
    if (password?.length >= 12) strength++;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) strength++;
    if (/\d/?.test(password)) strength++;
    if (/[@$!%*?&]/?.test(password)) strength++;

    const levels = [
      { strength: 0, label: '', color: '' },
      { strength: 1, label: '매우 약함', color: 'bg-error' },
      { strength: 2, label: '약함', color: 'bg-warning' },
      { strength: 3, label: '보통', color: 'bg-accent' },
      { strength: 4, label: '강함', color: 'bg-success' },
      { strength: 5, label: '매우 강함', color: 'bg-success' }
    ];

    return levels?.[strength];
  };

  const passwordStrength = getPasswordStrength();

  return (
    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
      <ImageUploadZone
        onImageSelect={handleAvatarSelect}
        selectedImage={avatarImage}
        error={errors?.avatar}
        title="프로필 이미지 업로드(선택)"
        selectedAlt="선택한 프로필 이미지"
      />

      <Input
        label="사용자 이름"
        type="text"
        name="username"
        placeholder="닉네임을 입력하세요"
        value={formData?.username}
        onChange={handleChange}
        error={errors?.username}
        required
      />
      <Input
        label="이메일"
        type="email"
        name="email"
        placeholder="example@challengearchive.com"
        value={formData?.email}
        onChange={handleChange}
        error={errors?.email}
        required
      />
      <div className="relative">
        <Input
          label="비밀번호"
          type={showPassword ? 'text' : 'password'}
          name="password"
          placeholder="8자 이상"
          value={formData?.password}
          onChange={handleChange}
          error={errors?.password}
          required
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-gaming"
          aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
        </button>
      </div>
      {formData?.password && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs md:text-sm">
            <span className="text-muted-foreground caption">비밀번호 강도:</span>
            <span className={`font-medium ${
              passwordStrength?.strength <= 2 ? 'text-warning' : 'text-success'
            }`}>
              {passwordStrength?.label}
            </span>
          </div>
          <div className="flex space-x-1">
            {[1, 2, 3, 4, 5]?.map((level) => (
              <div
                key={level}
                className={`h-1.5 flex-1 rounded-full transition-gaming ${
                  level <= passwordStrength?.strength
                    ? passwordStrength?.color
                    : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </div>
      )}
      <div className="relative">
        <Input
          label="비밀번호 확인"
          type={showConfirmPassword ? 'text' : 'password'}
          name="confirmPassword"
          placeholder="비밀번호를 다시 입력하세요"
          value={formData?.confirmPassword}
          onChange={handleChange}
          error={errors?.confirmPassword}
          required
        />
        <button
          type="button"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-gaming"
          aria-label={showConfirmPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
        >
          <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
        </button>
      </div>
      <div className="space-y-3 pt-2">
        <Checkbox
          label={(
            <span>
              <button
                type="button"
                className="text-primary underline underline-offset-2 hover:text-primary/80 transition-gaming font-semibold"
                onClick={() => setIsTermsModalOpen(true)}
              >
                이용약관
              </button>
              <span className="text-muted-foreground">에 동의합니다.</span>
            </span>
          )}
          checked={agreedToTerms}
          onChange={(e) => {
            setAgreedToTerms(e?.target?.checked);
            if (errors?.terms) {
              setErrors(prev => ({ ...prev, terms: '' }));
            }
          }}
          error={errors?.terms}
          required
        />

        <Checkbox
          label={(
            <span>
              <button
                type="button"
                className="text-primary underline underline-offset-2 hover:text-primary/80 transition-gaming font-semibold"
                onClick={() => setIsPrivacyModalOpen(true)}
              >
                개인정보처리방침
              </button>
              <span className="text-muted-foreground">에 동의합니다.</span>
            </span>
          )}
          checked={agreedToPrivacy}
          onChange={(e) => {
            setAgreedToPrivacy(e?.target?.checked);
            if (errors?.privacy) {
              setErrors(prev => ({ ...prev, privacy: '' }));
            }
          }}
          error={errors?.privacy}
          required
        />
        
        {(errors?.terms || errors?.privacy) && (
          <p className="text-xs md:text-sm text-destructive font-semibold">
            {errors?.terms || errors?.privacy}
          </p>
        )}
      </div>
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isLoading}
        iconName="UserPlus"
        iconPosition="right"
        className="mt-6 md:mt-8"
      >
        회원가입
      </Button>
      <p className="text-xs md:text-sm text-center text-muted-foreground caption mt-4">
        이미 계정이 있으신가요?{' '}
        <button
          type="button"
          className="text-primary hover:text-primary/80 transition-gaming font-medium"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          로그인하기
        </button>
      </p>

      {notice?.open && (
        <div className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeNotice}>
          <div className="w-full max-w-md bg-card rounded-lg border border-border shadow-gaming-lg" onClick={(e) => e?.stopPropagation()}>
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="text-base md:text-lg font-heading font-semibold text-foreground">{notice?.title}</div>
              <button
                type="button"
                className="px-3 py-2 rounded-md hover:bg-muted transition-gaming text-sm"
                onClick={closeNotice}
              >
                닫기
              </button>
            </div>
            <div className="p-5">
              <div
                className={`text-sm leading-relaxed ${
                  notice?.variant === 'error'
                    ? 'text-destructive'
                    : notice?.variant === 'success'
                      ? 'text-success'
                      : notice?.variant === 'warning'
                        ? 'text-warning'
                        : 'text-foreground'
                }`}
              >
                {notice?.message}
              </div>
              <div className="mt-6 flex justify-end">
                <Button type="button" variant="default" onClick={closeNotice}>
                  {notice?.primaryLabel || '확인'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isTermsModalOpen && (
        <div
          className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setIsTermsModalOpen(false)}
        >
          <div
            className="w-full max-w-2xl bg-card rounded-lg border border-border shadow-gaming-lg"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="text-base md:text-lg font-heading font-semibold text-foreground">이용약관</div>
              <button
                type="button"
                className="px-3 py-2 rounded-md hover:bg-muted transition-gaming text-sm"
                onClick={() => setIsTermsModalOpen(false)}
              >
                닫기
              </button>
            </div>
            <div className="p-5 max-h-[70vh] overflow-y-auto space-y-4">
              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">1. 서비스 개요</div>
                <div className="text-muted-foreground">
                  kkumhyeonLabs는 이미지 기반 증명, 별점, 업적 시스템을 통해 목표 달성을 기록하고 공유할 수 있는 서비스입니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">2. 계정 및 이용</div>
                <div className="text-muted-foreground">
                  이용자는 정확한 정보를 바탕으로 계정을 생성하고, 본인 계정의 보안을 유지할 책임이 있습니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">3. 콘텐츠(증명 이미지 등)</div>
                <div className="text-muted-foreground">
                  이용자는 본인이 업로드한 콘텐츠에 대한 권리/책임을 가지며, 불법/유해/타인의 권리를 침해하는 콘텐츠 업로드를 금지합니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">4. 신고 및 제재</div>
                <div className="text-muted-foreground">
                  신고된 콘텐츠는 운영 정책에 따라 검토될 수 있으며, 필요 시 공개 범위 제한 또는 계정 이용 제한이 이루어질 수 있습니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">5. 서비스 제공 및 변경</div>
                <div className="text-muted-foreground">
                  운영자는 서비스의 전부 또는 일부를 변경/중단할 수 있으며, 중요한 변경 사항은 서비스 내 공지 등 합리적인 방법으로 안내합니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">6. 금지행위</div>
                <div className="text-muted-foreground">
                  타인의 권리 침해, 불법 정보 게시, 서비스 장애 유발(과도한 요청/비정상 접근), 허위 신고, 계정 도용 등은 금지되며 위반 시 이용 제한이 있을 수 있습니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">7. 지식재산권(저작권 등)</div>
                <div className="text-muted-foreground">
                  서비스 및 서비스 내 제공되는 콘텐츠에 대한 권리는 운영자 또는 정당한 권리자에게 귀속됩니다. 이용자가 업로드한 콘텐츠의 권리는 이용자에게 귀속되며, 서비스 운영/노출을 위해 필요한 범위에서 이용(저장/표시/전송)될 수 있습니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">8. 면책 및 책임 제한</div>
                <div className="text-muted-foreground">
                  운영자는 천재지변, 불가항력, 이용자 귀책 사유로 인한 서비스 장애에 대해 책임을 지지 않으며, 이용자가 게시한 콘텐츠로 인해 발생하는 분쟁은 당사자 간 해결을 원칙으로 합니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">9. 이용 해지(탈퇴)</div>
                <div className="text-muted-foreground">
                  이용자는 언제든지 탈퇴할 수 있으며, 관련 법령 및 개인정보처리방침에 따라 일정 기간 보관이 필요한 정보를 제외하고 삭제됩니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">10. 준거법 및 분쟁</div>
                <div className="text-muted-foreground">
                  본 약관과 관련된 사항은 대한민국 법령을 준거법으로 하며, 분쟁 발생 시 관할 법원은 관련 법령에 따릅니다.
                </div>
              </div>

              <div className="text-xs text-muted-foreground caption">
                본 약관은 심플 버전이며, 서비스 운영 과정에서 업데이트될 수 있습니다.
              </div>
            </div>
          </div>
        </div>
      )}

      {isPrivacyModalOpen && (
        <div
          className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setIsPrivacyModalOpen(false)}
        >
          <div
            className="w-full max-w-2xl bg-card rounded-lg border border-border shadow-gaming-lg"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="text-base md:text-lg font-heading font-semibold text-foreground">개인정보처리방침</div>
              <button
                type="button"
                className="px-3 py-2 rounded-md hover:bg-muted transition-gaming text-sm"
                onClick={() => setIsPrivacyModalOpen(false)}
              >
                닫기
              </button>
            </div>
            <div className="p-5 max-h-[70vh] overflow-y-auto space-y-4">
              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">1. 수집 항목</div>
                <div className="text-muted-foreground">
                  이메일, 사용자 이름, 프로필 이미지(선택), 서비스 이용 과정에서 생성되는 콘텐츠(증명 이미지/텍스트/별점 등)가 포함될 수 있습니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">2. 이용 목적</div>
                <div className="text-muted-foreground">
                  회원 식별/로그인, 서비스 제공(프로젝트/챌린지/증명 기능), 고객 지원, 부정 이용 방지, 신고 처리에 활용됩니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">3. 보관 및 삭제</div>
                <div className="text-muted-foreground">
                  서비스 제공에 필요한 기간 동안 보관되며, 관련 법령 또는 내부 정책에 따라 보관 후 삭제될 수 있습니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">4. 공개 범위</div>
                <div className="text-muted-foreground">
                  이용자가 공개로 설정한 정보/콘텐츠만 커뮤니티 영역에 표시됩니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">5. 제3자 제공</div>
                <div className="text-muted-foreground">
                  운영자는 원칙적으로 이용자의 개인정보를 제3자에게 제공하지 않습니다. 다만 법령에 근거가 있거나 수사기관의 적법한 요청이 있는 경우 등 예외적으로 제공될 수 있습니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">6. 처리위탁 및 서비스 제공자</div>
                <div className="text-muted-foreground">
                  서비스 운영을 위해 인증/데이터베이스/파일 저장 등 기능을 외부 서비스 제공자(예: Supabase)에 위탁하여 처리할 수 있습니다. 위탁 시 관련 법령에 따라 안전하게 관리·감독합니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">7. 국외 이전 가능성</div>
                <div className="text-muted-foreground">
                  서비스 인프라 제공자의 서버 위치에 따라 개인정보가 국외로 이전(저장/처리)될 수 있습니다. 국외 이전이 발생하는 경우 관련 법령에 따라 필요한 고지/동의를 포함한 절차를 준수합니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">8. 이용자의 권리</div>
                <div className="text-muted-foreground">
                  이용자는 개인정보 열람/정정/삭제/처리정지 요구 등 관련 법령이 정한 권리를 행사할 수 있습니다. 일부 정보는 서비스 제공에 필수이므로 삭제/처리정지 시 서비스 이용이 제한될 수 있습니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">9. 안전성 확보 조치</div>
                <div className="text-muted-foreground">
                  운영자는 개인정보 보호를 위해 접근 통제, 권한 관리, 암호화(가능한 범위), 로그 관리 등 합리적인 기술적/관리적 보호조치를 적용합니다.
                </div>
              </div>

              <div className="text-sm text-foreground">
                <div className="font-semibold mb-2">10. 문의처 및 변경</div>
                <div className="text-muted-foreground">
                  개인정보 관련 문의는 서비스 내 안내된 연락처(예: 이메일)로 접수할 수 있으며, 방침 변경 시 서비스 내 공지 등 합리적인 방법으로 안내합니다.
                </div>
              </div>

              <div className="text-xs text-muted-foreground caption">
                본 방침은 심플 버전이며, 서비스 운영 과정에서 업데이트될 수 있습니다.
              </div>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default RegisterForm;